// lib/services/compute/character_builder_gpu.ts

import { ModelData } from "../geometry/modeldata";
import AnimationLibrary from "../animation/animation_library";

// Match the WGSL header constants in animation_types.wgsl.
//
// SkeletonNode layout (16-byte aligned, total 64 bytes per node):
//   parent_index: i32
//   pad0, pad1, pad2: i32
//   bind_translation: vec4<f32>   (xyz, w unused)
//   bind_rotation:    vec4<f32>   (xyzw quaternion)
//   bind_scale:       vec4<f32>   (xyz, w unused)
const SKELETON_NODE_STRIDE_FLOATS = 16; // 64 bytes

// ClipDescriptor layout (32 bytes):
//   duration: f32
//   loop_flag: u32
//   root_motion_node_index: i32
//   channel_start: u32
//   channel_count: u32
//   pad0, pad1, pad2: u32
const CLIP_DESCRIPTOR_STRIDE_FLOATS = 8;

// ChannelDescriptor layout (32 bytes):
//   node_index: i32
//   path: u32              (0=translation, 1=rotation, 2=scale)
//   interpolation: u32     (0=STEP, 1=LINEAR, 2=CUBICSPLINE)
//   key_count: u32
//   times_offset: u32      (in floats)
//   values_offset: u32     (in floats)
//   pad0, pad1: u32
const CHANNEL_DESCRIPTOR_STRIDE_FLOATS = 8;

// CharacterConstants layout (16 bytes uniform):
//   mesh_node_index: u32
//   node_count: u32
//   joint_count: u32
//   clip_count: u32
const CHARACTER_CONSTANTS_BYTES = 16;

const PATH_TRANSLATION = 0;
const PATH_ROTATION = 1;
const PATH_SCALE = 2;

const INTERPOLATION_STEP = 0;
const INTERPOLATION_LINEAR = 1;
const INTERPOLATION_CUBICSPLINE = 2;

function pathToCode(path: string): number {
  switch (path) {
    case "translation":
      return PATH_TRANSLATION;
    case "rotation":
      return PATH_ROTATION;
    case "scale":
      return PATH_SCALE;
    default:
      throw new Error(`Unknown channel path: ${path}`);
  }
}

function interpolationToCode(mode: string): number {
  switch (mode) {
    case "STEP":
      return INTERPOLATION_STEP;
    case "LINEAR":
      return INTERPOLATION_LINEAR;
    case "CUBICSPLINE":
      return INTERPOLATION_CUBICSPLINE;
    default:
      return INTERPOLATION_LINEAR;
  }
}

export default class CharacterBuilderGpu {
  key: string;

  skeleton_buffer: GPUBuffer | null = null;
  inverse_bind_buffer: GPUBuffer | null = null;
  joint_map_buffer: GPUBuffer | null = null;
  clips_buffer: GPUBuffer | null = null;
  channels_buffer: GPUBuffer | null = null;
  times_buffer: GPUBuffer | null = null;
  values_buffer: GPUBuffer | null = null;
  character_constants_buffer: GPUBuffer | null = null;

  node_count = 0;
  joint_count = 0;
  clip_count = 0;
  channel_count = 0;
  mesh_node_index = 0;

  constructor(key: string) {
    this.key = key;
  }

  async build(
    device: GPUDevice,
    model: ModelData,
    library: AnimationLibrary,
    skin_index: number,
    mesh_node_index: number
  ): Promise<void> {
    this.mesh_node_index = mesh_node_index;

    this.buildSkeletonBuffer(device, model);
    this.buildSkinBuffers(device, model, skin_index);
    this.buildClipBuffers(device, library);
    this.buildCharacterConstantsBuffer(device);
  }

  private buildSkeletonBuffer(device: GPUDevice, model: ModelData): void {
    const node_count = model.nodes.length;
    this.node_count = node_count;

    const node_data = new ArrayBuffer(
      node_count * SKELETON_NODE_STRIDE_FLOATS * 4
    );
    const node_view_i32 = new Int32Array(node_data);
    const node_view_f32 = new Float32Array(node_data);

    for (let i = 0; i < node_count; i++) {
      const node = model.nodes[i];
      const base = i * SKELETON_NODE_STRIDE_FLOATS;

      // parent_index + 3 pads
      node_view_i32[base + 0] = node.parentIndex;
      node_view_i32[base + 1] = 0;
      node_view_i32[base + 2] = 0;
      node_view_i32[base + 3] = 0;

      // bind_translation (xyz, w=0)
      node_view_f32[base + 4] = node.translation[0];
      node_view_f32[base + 5] = node.translation[1];
      node_view_f32[base + 6] = node.translation[2];
      node_view_f32[base + 7] = 0;

      // bind_rotation (xyzw)
      node_view_f32[base + 8] = node.rotation[0];
      node_view_f32[base + 9] = node.rotation[1];
      node_view_f32[base + 10] = node.rotation[2];
      node_view_f32[base + 11] = node.rotation[3];

      // bind_scale (xyz, w=0)
      node_view_f32[base + 12] = node.scale[0];
      node_view_f32[base + 13] = node.scale[1];
      node_view_f32[base + 14] = node.scale[2];
      node_view_f32[base + 15] = 0;
    }

    this.skeleton_buffer = device.createBuffer({
      label: `${this.key}_skeleton`,
      size: node_data.byteLength,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });

    device.queue.writeBuffer(this.skeleton_buffer, 0, node_data);
  }

  private buildSkinBuffers(
    device: GPUDevice,
    model: ModelData,
    skin_index: number
  ): void {
    const skin = model.skins[skin_index];
    if (!skin) {
      throw new Error(
        `CharacterBuilderGpu: invalid skin index ${skin_index}`
      );
    }

    const joint_count = skin.joints.length;
    this.joint_count = joint_count;

    // Joint map: u32 per joint = node index
    const joint_map = new Uint32Array(joint_count);
    for (let i = 0; i < joint_count; i++) {
      joint_map[i] = skin.joints[i];
    }

    this.joint_map_buffer = device.createBuffer({
      label: `${this.key}_joint_map`,
      size: joint_map.byteLength,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });
    device.queue.writeBuffer(this.joint_map_buffer, 0, joint_map);

    // Inverse bind matrices: 16 floats per joint
    const inv_bind = new Float32Array(joint_count * 16);
    for (let i = 0; i < joint_count; i++) {
      const m = skin.inverseBindMatrices[i];
      inv_bind.set(m, i * 16);
    }

    this.inverse_bind_buffer = device.createBuffer({
      label: `${this.key}_inverse_bind`,
      size: inv_bind.byteLength,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });
    device.queue.writeBuffer(this.inverse_bind_buffer, 0, inv_bind);
  }

  private buildClipBuffers(
    device: GPUDevice,
    library: AnimationLibrary
  ): void {
    const clips = library.getAll();
    const clip_count = clips.length;
    this.clip_count = clip_count;

    // First pass: count totals so we can allocate the channel,
    // times, and values arrays exactly once.
    let total_channel_count = 0;
    let total_times_floats = 0;
    let total_values_floats = 0;

    for (let c = 0; c < clip_count; c++) {
      const clip = clips[c];
      total_channel_count += clip.channels.length;

      for (let k = 0; k < clip.channels.length; k++) {
        const ch = clip.channels[k];
        total_times_floats += ch.times.length;
        total_values_floats += ch.values.length;
      }
    }

    this.channel_count = total_channel_count;

    const clip_data = new ArrayBuffer(
      clip_count * CLIP_DESCRIPTOR_STRIDE_FLOATS * 4
    );
    const clip_view_f32 = new Float32Array(clip_data);
    const clip_view_u32 = new Uint32Array(clip_data);
    const clip_view_i32 = new Int32Array(clip_data);

    const channel_data = new ArrayBuffer(
      total_channel_count * CHANNEL_DESCRIPTOR_STRIDE_FLOATS * 4
    );
    const channel_view_u32 = new Uint32Array(channel_data);
    const channel_view_i32 = new Int32Array(channel_data);

    const times_array = new Float32Array(total_times_floats);
    const values_array = new Float32Array(total_values_floats);

    let channel_cursor = 0;
    let times_cursor = 0;
    let values_cursor = 0;

    for (let c = 0; c < clip_count; c++) {
      const clip = clips[c];
      const clip_base = c * CLIP_DESCRIPTOR_STRIDE_FLOATS;

      clip_view_f32[clip_base + 0] = clip.duration;
      clip_view_u32[clip_base + 1] = 0;
      clip_view_i32[clip_base + 2] = clip.rootMotionNodeIndex;
      clip_view_u32[clip_base + 3] = channel_cursor;
      clip_view_u32[clip_base + 4] = clip.channels.length;
      clip_view_u32[clip_base + 5] = 0;
      clip_view_u32[clip_base + 6] = 0;
      clip_view_u32[clip_base + 7] = 0;

      for (let k = 0; k < clip.channels.length; k++) {
        const ch = clip.channels[k];
        const path_code = pathToCode(ch.path);
        const interp_code = interpolationToCode(ch.interpolation);

        const channel_base =
          (channel_cursor + k) * CHANNEL_DESCRIPTOR_STRIDE_FLOATS;

        channel_view_i32[channel_base + 0] = ch.nodeIndex;
        channel_view_u32[channel_base + 1] = path_code;
        channel_view_u32[channel_base + 2] = interp_code;
        channel_view_u32[channel_base + 3] = ch.times.length;
        channel_view_u32[channel_base + 4] = times_cursor;
        channel_view_u32[channel_base + 5] = values_cursor;
        channel_view_u32[channel_base + 6] = 0;
        channel_view_u32[channel_base + 7] = 0;

        times_array.set(ch.times, times_cursor);
        values_array.set(ch.values, values_cursor);

        times_cursor += ch.times.length;
        values_cursor += ch.values.length;
      }

      channel_cursor += clip.channels.length;
    }

    this.clips_buffer = device.createBuffer({
      label: `${this.key}_clips`,
      size: clip_data.byteLength,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });
    device.queue.writeBuffer(this.clips_buffer, 0, clip_data);

    this.channels_buffer = device.createBuffer({
      label: `${this.key}_channels`,
      size: channel_data.byteLength,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });
    device.queue.writeBuffer(this.channels_buffer, 0, channel_data);

    this.times_buffer = device.createBuffer({
      label: `${this.key}_times`,
      size: times_array.byteLength,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });
    device.queue.writeBuffer(this.times_buffer, 0, times_array);

    this.values_buffer = device.createBuffer({
      label: `${this.key}_values`,
      size: values_array.byteLength,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });
    device.queue.writeBuffer(this.values_buffer, 0, values_array);
  }

  private buildCharacterConstantsBuffer(device: GPUDevice): void {
    const constants_data = new ArrayBuffer(CHARACTER_CONSTANTS_BYTES);
    const constants_view = new Uint32Array(constants_data);

    constants_view[0] = this.mesh_node_index;
    constants_view[1] = this.node_count;
    constants_view[2] = this.joint_count;
    constants_view[3] = this.clip_count;

    this.character_constants_buffer = device.createBuffer({
      label: `${this.key}_character_constants`,
      size: CHARACTER_CONSTANTS_BYTES,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    device.queue.writeBuffer(this.character_constants_buffer, 0, constants_data);
  }

  shutdown(): void {
  if (this.skeleton_buffer) {
    this.skeleton_buffer.destroy();
    this.skeleton_buffer = null;
  }
  if (this.inverse_bind_buffer) {
    this.inverse_bind_buffer.destroy();
    this.inverse_bind_buffer = null;
  }
  if (this.joint_map_buffer) {
    this.joint_map_buffer.destroy();
    this.joint_map_buffer = null;
  }
  if (this.clips_buffer) {
    this.clips_buffer.destroy();
    this.clips_buffer = null;
  }
  if (this.channels_buffer) {
    this.channels_buffer.destroy();
    this.channels_buffer = null;
  }
  if (this.times_buffer) {
    this.times_buffer.destroy();
    this.times_buffer = null;
  }
  if (this.values_buffer) {
    this.values_buffer.destroy();
    this.values_buffer = null;
  }
  if (this.character_constants_buffer) {
    this.character_constants_buffer.destroy();
    this.character_constants_buffer = null;
  }

  this.node_count = 0;
  this.joint_count = 0;
  this.clip_count = 0;
  this.channel_count = 0;
  this.mesh_node_index = 0;
}
}